import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock3, Sparkles } from "lucide-react";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { fetchBlogPosts } from "../lib/api";
import RichText from "../components/RichText";
import { getTemplateMeta } from "../lib/blogTemplates";

function formatDate(value) {
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const Blogs = () => {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  useDocumentMeta({
    title: "FindClarity Blog | Mental Health, Reflection and Counseling Guides",
    description:
      "Explore FindClarity cornerstone content on mental health, emotional wellbeing, counseling support, and peaceful decision-making.",
  });

  useEffect(() => {
    let active = true;

    async function loadPosts() {
      try {
        const data = await fetchBlogPosts();
        if (!active) {
          return;
        }
        setPosts(data);
        setStatus("ready");
      } catch (loadError) {
        if (!active) {
          return;
        }
        setError("Unable to load blog posts right now.");
        setStatus("error");
      }
    }

    loadPosts();

    return () => {
      active = false;
    };
  }, []);

  const featuredPost =
    posts.find((post) => post.featured) || (posts.length > 0 ? posts[0] : null);
  const secondaryPosts = featuredPost
    ? posts.filter((post) => post.id !== featuredPost.id)
    : [];

  return (
    <div className="blogs-page">
      <section className="blogs-hero">
        <div className="blogs-hero-inner">
          <div className="blogs-eyebrow">
            <Sparkles size={16} />
            Cornerstone Content Library
          </div>
          <h1 className="blogs-hero-title">
            Practical articles on mental wellness, calm thinking, and finding
            clarity.
          </h1>
          <p className="blogs-hero-copy">
            This is the growing knowledge hub for FindClarity. Each article is
            designed to feel like a guided session: clear, supportive, and easy
            to return to when life feels noisy.
          </p>
          <div className="blogs-hero-actions">
            <a href="#blog-library" className="blogs-secondary-action">
              Browse Articles
            </a>
          </div>
        </div>
      </section>

      <section className="blog-library-section" id="blog-library">
        <div className="blog-library-shell">
          {status === "loading" && (
            <div className="blog-empty-state">
              <BookOpen size={28} />
              <h2>Loading your articles</h2>
              <p>We’re pulling the latest content from your blog library.</p>
            </div>
          )}

          {status === "error" && (
            <div className="blog-empty-state">
              <BookOpen size={28} />
              <h2>Blog library unavailable</h2>
              <p>{error}</p>
            </div>
          )}

          {status === "ready" && posts.length === 0 && (
            <div className="blog-empty-state">
              <BookOpen size={28} />
              <h2>No blogs published yet</h2>
              <p>
                Use the dashboard to add your first cornerstone article with a
                hero image, sections, and supporting visuals.
              </p>
            </div>
          )}

          {status === "ready" && featuredPost && (
            <>
              <article className="featured-blog-card">
                <div className="featured-blog-media">
                  <img
                    src={featuredPost.hero_image}
                    alt={featuredPost.hero_image_alt}
                    className="featured-blog-image"
                  />
                </div>
                <div className="featured-blog-content">
                  <div className="blog-chip-row">
                    <span className="blog-chip">{featuredPost.category}</span>
                    <span className="blog-chip blog-chip-template">
                      {getTemplateMeta(featuredPost.template).label}
                    </span>
                  </div>
                  <RichText as="h2" value={featuredPost.title} multiline={false} />
                  <RichText
                    className="blog-featured-excerpt"
                    value={featuredPost.excerpt}
                  />
                  <div className="blog-meta-row">
                    <span>{formatDate(featuredPost.published_at)}</span>
                    <span className="blog-meta-dot" />
                    <span>{featuredPost.read_time}</span>
                    <span className="blog-meta-dot" />
                    <span>{featuredPost.author}</span>
                  </div>
                  <Link
                    to={`/blogs/${featuredPost.slug}`}
                    className="featured-blog-link"
                  >
                    Read article
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </article>

              <div className="blog-list-grid">
                {secondaryPosts.map((post) => (
                  <article className="blog-list-card" key={post.id}>
                    <img
                      src={post.hero_image}
                      alt={post.hero_image_alt}
                      className="blog-list-image"
                    />
                    <div className="blog-list-card-body">
                      <div className="blog-chip-row">
                        <span className="blog-chip">{post.category}</span>
                        <span className="blog-chip blog-chip-template">
                          {getTemplateMeta(post.template).label}
                        </span>
                      </div>
                      <RichText as="h3" value={post.title} multiline={false} />
                      <RichText className="blog-list-excerpt" value={post.excerpt} />
                      <div className="blog-list-meta">
                        <span>{formatDate(post.published_at)}</span>
                        <span className="blog-meta-dot" />
                        <Clock3 size={14} />
                        <span>{post.read_time}</span>
                      </div>
                      <Link to={`/blogs/${post.slug}`} className="blog-list-link">
                        Explore article
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
